import { create } from 'zustand';
import { apiFetch, getFreshAccessToken } from '../utils/api';
import { emitSSEEvent } from '../lib/sseEventBus';

// ============================================================
// NOTIFICATION STORE
// ============================================================
// Manages in-app notifications with real-time updates via SSE
// ============================================================

export type NotificationType = 
  | 'campaign_progress'
  | 'campaign_complete'
  | 'new_reply'
  | 'meeting_booked'
  | 'team_invite'
  | 'team_joined'
  | 'team_role_change'
  | 'dedup_alert'
  | 'system';

export interface Notification {
  id: string;
  user_id: string;
  team_id?: string;
  type: NotificationType;
  title: string;
  message: string;
  action_url?: string;
  action_label?: string;
  related_entity_type?: string;
  related_entity_id?: string;
  metadata: Record<string, any>;
  read: boolean;
  read_at?: string;
  dismissed: boolean;
  priority: number;
  created_at: string;
}

export interface NotificationSummary {
  unread_count: number;
  unread_replies: number;
  unread_meetings: number;
  unread_alerts: number;
  latest_unread_at?: string;
}

let notificationEventSource: EventSource | null = null;
let notificationSseConnecting = false;

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  summary: NotificationSummary | null;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  isSubscribed: boolean;
  
  // Actions
  fetchNotifications: (options?: { refresh?: boolean }) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  fetchSummary: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  addNotification: (notification: Notification) => void;
  subscribeToRealtime: () => () => void;
  clearNotifications: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  summary: null,
  loading: false,
  error: null,
  hasMore: true,
  isSubscribed: false,

  // ============================================================
  // FETCH NOTIFICATIONS
  // ============================================================

  fetchNotifications: async (options = {}) => {
    const { refresh = false } = options;
    const currentNotifications = get().notifications;
    
    if (get().loading) return;
    
    set({ loading: true, error: null });

    try {
      const offset = refresh ? 0 : currentNotifications.length;
      const response = await apiFetch(`/api/notifications?limit=20&offset=${offset}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();
      const newNotifications = data.notifications || [];
      
      set({
        notifications: refresh 
          ? newNotifications 
          : [...currentNotifications, ...newNotifications],
        hasMore: newNotifications.length === 20,
        loading: false,
      });

      // Also update unread count
      get().fetchUnreadCount();
    } catch (error) {
      console.error('[NOTIFICATION-STORE] Fetch failed:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load notifications',
        loading: false,
      });
    }
  },

  // ============================================================
  // FETCH UNREAD COUNT
  // ============================================================

  fetchUnreadCount: async () => {
    try {
      const response = await apiFetch('/api/notifications/unread-count');
      
      if (response.ok) {
        const data = await response.json();
        set({ unreadCount: data.count || 0 });
      }
    } catch (error) {
      console.error('[NOTIFICATION-STORE] Failed to fetch unread count:', error);
    }
  },

  // ============================================================
  // FETCH SUMMARY
  // ============================================================

  fetchSummary: async () => {
    try {
      const response = await apiFetch('/api/notifications/summary');
      
      if (response.ok) {
        const data = await response.json();
        set({ 
          summary: data,
          unreadCount: data.unread_count || 0,
        });
      }
    } catch (error) {
      console.error('[NOTIFICATION-STORE] Failed to fetch summary:', error);
    }
  },

  // ============================================================
  // MARK AS READ
  // ============================================================

  markAsRead: async (id: string) => {
    try {
      const response = await apiFetch(`/api/notifications/${id}/read`, {
        method: 'PATCH',
      });

      if (response.ok) {
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true, read_at: new Date().toISOString() } : n
          ),
          unreadCount: Math.max(0, state.unreadCount - 1),
        }));
      }
    } catch (error) {
      console.error('[NOTIFICATION-STORE] Failed to mark as read:', error);
    }
  },

  // ============================================================
  // MARK ALL AS READ
  // ============================================================

  markAllAsRead: async () => {
    try {
      const response = await apiFetch('/api/notifications/mark-all-read', {
        method: 'POST',
      });

      if (response.ok) {
        set((state) => ({
          notifications: state.notifications.map((n) => ({
            ...n,
            read: true,
            read_at: n.read_at || new Date().toISOString(),
          })),
          unreadCount: 0,
        }));
      }
    } catch (error) {
      console.error('[NOTIFICATION-STORE] Failed to mark all as read:', error);
    }
  },

  // ============================================================
  // DISMISS NOTIFICATION
  // ============================================================

  dismiss: async (id: string) => {
    try {
      const response = await apiFetch(`/api/notifications/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        const notification = get().notifications.find((n) => n.id === id);
        const wasUnread = notification && !notification.read;

        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
          unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
        }));
      }
    } catch (error) {
      console.error('[NOTIFICATION-STORE] Failed to dismiss:', error);
    }
  },

  // ============================================================
  // ADD NOTIFICATION (from SSE)
  // ============================================================

  addNotification: (notification: Notification) => {
    if (!notification?.id || typeof notification.title !== 'string') {
      return;
    }

    set((state) => {
      if (state.notifications.some((n) => n.id === notification.id)) {
        return state;
      }

      return {
        notifications: [notification, ...state.notifications],
        unreadCount: state.unreadCount + 1,
      };
    });
  },

  // ============================================================
  // SUBSCRIBE TO REALTIME (SSE)
  // ============================================================

  subscribeToRealtime: () => {
    if (get().isSubscribed || notificationSseConnecting || notificationEventSource) {
      console.log('[NOTIFICATION-STORE] subscribeToRealtime: already subscribed, returning noop');
      return () => {}; // Already subscribed
    }

    const apiBase = (import.meta as any).env?.VITE_API_URL || '';
    // EventSource can't set custom headers (no `Authorization: Bearer …`),
    // and in cross-origin dev the halr_session cookie isn't sent (frontend
    // localhost:5173 vs backend 127.0.0.1:10000 → SameSite=Lax blocks +
    // different hosts mean the cookie's host-binding doesn't match). So we
    // pass a fresh Supabase access token as `?token=`, and the server's
    // userAuth middleware accepts it for `/api/notifications/subscribe`
    // (same pattern it already uses for `/api/realtime/*`). Same token
    // value as the Bearer header on REST calls — no new auth surface.
    const tConnectStart = Date.now();
    let stopped = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let errorCount = 0;
    notificationSseConnecting = true;

    // Defer JSON.parse + store mutation off the main event loop so the
    // notification handler doesn't trigger Chrome's "'message' handler took
    // 153-156ms" violations during an active session. The parse itself is
    // cheap; the work is fanning out to store subscribers (which re-render
    // bell badges, launchpad cards, etc). requestIdleCallback yields to
    // any pending paint/input first.
    const scheduleIdle =
      typeof window !== 'undefined' && typeof (window as any).requestIdleCallback === 'function'
        ? (cb: () => void) => (window as any).requestIdleCallback(cb, { timeout: 500 })
        : (cb: () => void) => setTimeout(cb, 0);

    const connect = async (forceRefresh = false) => {
      if (stopped) return;
      notificationSseConnecting = true;
      const accessToken = await getFreshAccessToken({ forceRefresh });
      if (stopped) return;

      const url = accessToken
        ? `${apiBase}/api/notifications/subscribe?token=${encodeURIComponent(accessToken)}`
        : `${apiBase}/api/notifications/subscribe`;

      console.log('[NOTIFICATION-STORE] SSE connect attempt', {
        urlBase: `${apiBase}/api/notifications/subscribe`,
        apiBase: apiBase || '(same-origin)',
        sameOrigin: !apiBase || (typeof window !== 'undefined' && url.startsWith(window.location.origin)),
        hasAccessToken: !!accessToken,
        accessTokenLength: accessToken ? accessToken.length : 0,
        docCookieKeys: typeof document !== 'undefined'
          ? document.cookie.split(';').map(s => s.trim().split('=')[0]).filter(Boolean)
          : [],
        // Note: HttpOnly cookies (halr_session) aren't visible to JS — their
        // absence from docCookieKeys is expected, not a problem.
        withCredentials: true,
      });

      const eventSource = new EventSource(url, {
        withCredentials: true,
      });
      notificationEventSource = eventSource;

      eventSource.onopen = () => {
        notificationSseConnecting = false;
        errorCount = 0;
        console.log('[NOTIFICATION-STORE] SSE connected', {
          readyState: eventSource.readyState,
          elapsedMs: Date.now() - tConnectStart,
        });
        set({ isSubscribed: true });
      };

      eventSource.addEventListener('notification', (event) => {
        // Stash the raw string and defer the work — the SSE handler returns
        // in <1ms instead of holding the main thread for 150+ms.
        const raw = event.data;
        scheduleIdle(() => {
          try {
            const data = JSON.parse(raw);
            if (data.notification) {
              const notif = data.notification;
              console.log('[NOTIFICATION-STORE] Received notification:', notif.type);
              emitSSEEvent(notif.type, notif);
              get().addNotification(notif);
            }
          } catch (error) {
            console.error('[NOTIFICATION-STORE] Failed to parse SSE event:', error);
          }
        });
      });

      eventSource.onerror = (error) => {
        errorCount++;
        console.warn('[NOTIFICATION-STORE] SSE error', {
          errorCount,
          readyState: eventSource.readyState,
          readyStateLabel: ['CONNECTING', 'OPEN', 'CLOSED'][eventSource.readyState] || 'UNKNOWN',
          elapsedMs: Date.now() - tConnectStart,
          eventType: (error as any)?.type || 'error',
        });

        // EventSource retries reuse the original URL, so a stale query token
        // would keep failing. Rebuild the stream with a freshly refreshed token.
        eventSource.close();
        if (notificationEventSource === eventSource) notificationEventSource = null;
        notificationSseConnecting = false;
        set({ isSubscribed: false });

        if (!stopped) {
          reconnectTimer = setTimeout(() => {
            void connect(true);
          }, Math.min(30_000, 1000 * 2 ** Math.min(errorCount, 5)));
        }
      };
    };

    void connect();

    // Return cleanup function
    return () => {
      console.log('[NOTIFICATION-STORE] SSE disconnecting', {
        errorsObserved: errorCount,
        finalReadyState: notificationEventSource?.readyState,
        totalElapsedMs: Date.now() - tConnectStart,
      });
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      notificationEventSource?.close();
      notificationEventSource = null;
      notificationSseConnecting = false;
      set({ isSubscribed: false });
    };
  },

  // ============================================================
  // CLEAR NOTIFICATIONS
  // ============================================================

  clearNotifications: () => {
    set({
      notifications: [],
      unreadCount: 0,
      summary: null,
      hasMore: true,
      error: null,
    });
  },
}));

// ============================================================
// NOTIFICATION TYPE HELPERS
// ============================================================

export const NOTIFICATION_TYPE_CONFIG: Record<NotificationType, {
  icon: string;
  color: string;
  bgColor: string;
}> = {
  campaign_progress: {
    icon: 'TrendingUp',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  campaign_complete: {
    icon: 'CheckCircle',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  new_reply: {
    icon: 'MessageSquare',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  meeting_booked: {
    icon: 'Calendar',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  team_invite: {
    icon: 'UserPlus',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  team_joined: {
    icon: 'Users',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  team_role_change: {
    icon: 'Shield',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  dedup_alert: {
    icon: 'AlertTriangle',
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
  },
  system: {
    icon: 'Info',
    color: 'text-neutral-600',
    bgColor: 'bg-neutral-100',
  },
};

/**
 * Format relative time for notification display
 */
export function formatNotificationTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default useNotificationStore;
